import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Calculator, TrendingUp, DollarSign, Percent, Info } from 'lucide-react';
import NavigationControls from '../components/NavigationControls';
import PartieBadge from '../components/PartieBadge';
import { useLanguage } from '../contexts/LanguageContext';
import { ENFANTS_PAR_PARENT, CHECK_PAR_PARENT, JOURS_MOIS_SCOL, APP_PRIX, SEZZLE_NET, SAAS_PRIX, tauxParAnnee, opexDetail, tarification, abonnementParent, unitEconomicsEcole, unitEconomicsGarderie, unitEconomicsParTraiteur } from '../mock';
import { formatCurrency, formatCompact, formatCompactMobile } from '../lib/utils';
import { useIsMobile } from '../hooks/useIsMobile';

export default function ModelePage() {
  const { language } = useLanguage();
  const isMobile = useIsMobile();
  const fmt = (v) => isMobile ? formatCompactMobile(v) : formatCompact(v);
  
  const ftMin = Math.min(...Object.values(tauxParAnnee).map(t => t.ftRate * 100)).toFixed(1);
  const ftMax = Math.max(...Object.values(tauxParAnnee).map(t => t.ftRate * 100)).toFixed(1);
  const sezzMin = Math.min(...Object.values(tauxParAnnee).map(t => t.sezzleUp * 100));
  const sezzMax = Math.max(...Object.values(tauxParAnnee).map(t => t.sezzleUp * 100));
  const appMin = Math.min(...Object.values(tauxParAnnee).map(t => t.appTx * 100));
  const appMax = Math.max(...Object.values(tauxParAnnee).map(t => t.appTx * 100));
  
  const formulas = language === 'fr' ? [
    { 
      formula: 'Parents actifs', 
      calc: `repas/jour / ${ENFANTS_PAR_PARENT}`,
      description: `${ENFANTS_PAR_PARENT} enfants par famille en moyenne (Stat. Canada). Ex: 10 000 repas/jour = ${Math.round(10000 / ENFANTS_PAR_PARENT).toLocaleString('fr-CA')} parents actifs.`
    },
    { 
      formula: 'Transactions/mois', 
      calc: `${CHECK_PAR_PARENT} x parents`,
      description: `40% des parents commandent chaque semaine (4x/mois), 60% commandent mensuellement (1x/mois). Moyenne pondérée : 0.40 x 4 + 0.60 x 1 = ${CHECK_PAR_PARENT} transactions par parent par mois.`
    },
    { 
      formula: 'GMV régulier', 
      calc: `repas/jour x ${JOURS_MOIS_SCOL} jours x prix`,
      description: `${JOURS_MOIS_SCOL} jours scolaires par mois. Prix unitaire varie de ${tauxParAnnee.Y1.prix}$ (Y1) à ${tauxParAnnee.Y5.prix}$ (Y5).`
    },
    { 
      formula: 'Sezzle (BNPL)', 
      calc: `+${sezzMin}% à +${sezzMax}% volume`,
      description: `Volume additionnel grâce au paiement fractionné. Croissance : ${sezzMin}% (Y1) → ${sezzMax}% (Y5). Net pour EcoLunch : ${(SEZZLE_NET * 100).toFixed(1)}% du GMV Sezzle.`
    }
  ] : [
    { 
      formula: 'Active parents', 
      calc: `meals/day / ${ENFANTS_PAR_PARENT}`,
      description: `${ENFANTS_PAR_PARENT} children per family on average (Stats Canada). Ex: 10,000 meals/day = ${Math.round(10000 / ENFANTS_PAR_PARENT).toLocaleString('en-CA')} active parents.`
    },
    { 
      formula: 'Transactions/month', 
      calc: `${CHECK_PAR_PARENT} x parents`,
      description: `40% of parents order weekly (4x/month), 60% order monthly (1x/month). Weighted average: 0.40 x 4 + 0.60 x 1 = ${CHECK_PAR_PARENT} transactions per parent per month.`
    },
    { 
      formula: 'Regular GMV', 
      calc: `meals/day x ${JOURS_MOIS_SCOL} days x price`,
      description: `${JOURS_MOIS_SCOL} school days per month. Unit price from $${tauxParAnnee.Y1.prix} (Y1) to $${tauxParAnnee.Y5.prix} (Y5).`
    },
    { 
      formula: 'Sezzle (BNPL)', 
      calc: `+${sezzMin}% to +${sezzMax}% volume`,
      description: `Additional volume from Buy Now Pay Later. Growth: ${sezzMin}% (Y1) → ${sezzMax}% (Y5). EcoLunch net: ${(SEZZLE_NET * 100).toFixed(1)}% of Sezzle GMV.`
    }
  ];
  
  return (
    <TooltipProvider>
      <div className="min-h-screen p-4 sm:p-6 lg:p-8">
        <div className="max-w-5xl mx-auto">
          <PartieBadge path="/modele" />
          <Card className="bg-black/60 border-lime-500/30 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-2xl sm:text-3xl lg:text-4xl text-white mb-2">
                {language === 'fr' ? "Modèle d'Affaires" : 'Business Model'}
              </CardTitle>
              <div className="h-1 w-16 sm:w-24 bg-gradient-to-r from-lime-500 to-cyan-500 rounded-full" />
            </CardHeader>
            <CardContent className="space-y-8">
              {/* === TARIFICATION TIERED + UNIT ECONOMICS === */}
              <div className="p-6 bg-gradient-to-br from-lime-500/15 via-cyan-500/10 to-transparent rounded-2xl border-2 border-lime-500/40" data-testid="modele-tiered-pricing">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 bg-lime-500 text-black text-[10px] font-bold rounded-full uppercase tracking-wider">
                    {language === 'fr' ? 'Tarification par tiers' : 'Tiered pricing'}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mb-4">
                  {language === 'fr' ? 'Marges Transactionnelles Nettes' : 'Net Transaction Margins'}
                </h3>
                <div className="overflow-x-auto mb-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left p-3 text-gray-400 font-semibold">{language === 'fr' ? 'Traiteurs' : 'Caterers'}</th>
                        <th className="text-right p-3 text-gray-400 font-semibold">{language === 'fr' ? 'Frais EcoLunch' : 'EcoLunch Fees'}</th>
                        <th className="text-right p-3 text-gray-400 font-semibold">{language === 'fr' ? 'Coût paiement' : 'Payment cost'}</th>
                        <th className="text-right p-3 text-gray-400 font-semibold">{language === 'fr' ? 'Marge nette' : 'Net margin'}</th>
                        <th className="text-right p-3 text-gray-400 font-semibold">SaaS</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-800 bg-cyan-500/5">
                        <td className="p-3 text-white font-medium">{language === 'fr' ? tarification.tier1.label : tarification.tier1.labelEn}</td>
                        <td className="p-3 text-right text-cyan-400 font-mono">2,9% + 0,30$</td>
                        <td className="p-3 text-right text-gray-300 font-mono">1,7% + 0,18$</td>
                        <td className="p-3 text-right text-lime-400 font-mono font-bold">1,2% + 0,12$</td>
                        <td className="p-3 text-right text-gray-300">0$</td>
                      </tr>
                      <tr className="border-b border-gray-800 bg-lime-500/5">
                        <td className="p-3 text-white font-medium">{language === 'fr' ? tarification.tier2.label : tarification.tier2.labelEn}</td>
                        <td className="p-3 text-right text-cyan-400 font-mono">3,9% + 0,30$</td>
                        <td className="p-3 text-right text-gray-300 font-mono">1,7% + 0,18$</td>
                        <td className="p-3 text-right text-lime-400 font-mono font-bold">2,2% + 0,12$</td>
                        <td className="p-3 text-right text-orange-400 font-semibold">{language === 'fr' ? 'min 150$/mois' : 'min $150/month'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-400 mb-4">
                  {language === 'fr'
                    ? 'Coût paiement détaillé : Interchange moyen 1,7% + Frais fixe 0,06$ + PSP 0,12$ = 1,7% + 0,18$'
                    : 'Detailed payment cost: Average interchange 1.7% + Fixed fee $0.06 + PSP $0.12 = 1.7% + $0.18'}
                </p>

                {/* Abonnement parent optionnel */}
                <div className="p-4 bg-black/40 rounded-lg border border-orange-500/30 mb-4">
                  <h4 className="text-sm font-bold text-orange-400 mb-2 uppercase tracking-wider">{language === 'fr' ? 'Abonnement parent (optionnel)' : 'Parent subscription (optional)'}</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div><p className="text-gray-400">{language === 'fr' ? 'Prix' : 'Price'}</p><p className="text-lime-400 font-bold text-base">{abonnementParent.prix}$/mois</p></div>
                    <div><p className="text-gray-400">{language === 'fr' ? 'Don écoles' : 'School donation'}</p><p className="text-cyan-400 font-bold text-base">{Math.round(abonnementParent.donationEcoles * 100)}% (1$)</p></div>
                    <div><p className="text-gray-400">{language === 'fr' ? 'Net EcoLunch' : 'EcoLunch net'}</p><p className="text-orange-400 font-bold text-base">{abonnementParent.netEcolunch}$/mois</p></div>
                    <div><p className="text-gray-400">{language === 'fr' ? 'Adoption' : 'Adoption'}</p><p className="text-purple-400 font-bold text-base">{Math.round(abonnementParent.adoptionParents * 100)}% {language === 'fr' ? 'parents' : 'parents'}</p></div>
                  </div>
                </div>

                {/* Unit Economics — Ecole + Garderie */}
                <h4 className="text-sm font-bold text-cyan-400 uppercase tracking-wider mb-3">{language === 'fr' ? 'Unit Economics annuels' : 'Annual Unit Economics'}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-4 bg-black/40 rounded-lg border border-cyan-500/30">
                    <p className="text-cyan-400 font-bold mb-2">{language === 'fr' ? 'Par école' : 'Per school'}</p>
                    <div className="text-xs space-y-1 mb-2 text-gray-300">
                      <p>{unitEconomicsEcole.repasJour} {language === 'fr' ? 'repas/jour' : 'meals/day'} • {unitEconomicsEcole.gmvAnnuel.toLocaleString()}$ GMV/an</p>
                      <p>{unitEconomicsEcole.transactionsAnnuelles.toLocaleString()} {language === 'fr' ? 'transactions/an' : 'transactions/year'}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div className="p-2 bg-cyan-500/10 rounded">
                        <p className="text-[10px] text-gray-400">Tier 1-25</p>
                        <p className="text-lime-400 font-bold">{unitEconomicsEcole.tier1.total.toLocaleString()}$</p>
                      </div>
                      <div className="p-2 bg-lime-500/10 rounded">
                        <p className="text-[10px] text-gray-400">Tier 26+</p>
                        <p className="text-lime-400 font-bold">{unitEconomicsEcole.tier2.total.toLocaleString()}$</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-black/40 rounded-lg border border-orange-500/30">
                    <p className="text-orange-400 font-bold mb-2">{language === 'fr' ? 'Par garderie' : 'Per daycare'}</p>
                    <div className="text-xs space-y-1 mb-2 text-gray-300">
                      <p>{unitEconomicsGarderie.repasJour} {language === 'fr' ? 'repas/jour' : 'meals/day'} • {unitEconomicsGarderie.gmvAnnuel.toLocaleString()}$ GMV/an</p>
                      <p>{language === 'fr' ? 'Commande moy.' : 'Avg. order'} : {unitEconomicsGarderie.commandeMoyenne.toLocaleString()}$ × {unitEconomicsGarderie.transactionsAnnuelles}/{language === 'fr' ? 'an' : 'year'}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div className="p-2 bg-orange-500/10 rounded">
                        <p className="text-[10px] text-gray-400">Tier 1-25</p>
                        <p className="text-lime-400 font-bold">{unitEconomicsGarderie.tier1.total.toLocaleString()}$</p>
                      </div>
                      <div className="p-2 bg-lime-500/10 rounded">
                        <p className="text-[10px] text-gray-400">Tier 26+</p>
                        <p className="text-lime-400 font-bold">{unitEconomicsGarderie.tier2.total.toLocaleString()}$</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Par type de traiteur */}
                <div className="mt-4">
                  <h4 className="text-sm font-bold text-purple-400 uppercase tracking-wider mb-3">{language === 'fr' ? 'Par empreinte traiteur' : 'Per caterer footprint'}</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th className="text-left p-2 text-gray-400">{language === 'fr' ? 'Type' : 'Type'}</th>
                          <th className="text-right p-2 text-gray-400">{language === 'fr' ? 'Repas/jour' : 'Meals/day'}</th>
                          <th className="text-right p-2 text-gray-400">{language === 'fr' ? 'GMV annuel' : 'Annual GMV'}</th>
                          <th className="text-right p-2 text-gray-400">Tier 1-25</th>
                          <th className="text-right p-2 text-gray-400">Tier 26+</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unitEconomicsParTraiteur.map((row, i) => (
                          <tr key={i} className="border-b border-gray-800">
                            <td className="p-2 text-white font-medium">{language === 'fr' ? row.type : row.typeEn}</td>
                            <td className="p-2 text-right text-gray-300">{row.repasJour.toLocaleString()}</td>
                            <td className="p-2 text-right text-cyan-400">{row.gmvAnnuel.toLocaleString()}$</td>
                            <td className="p-2 text-right text-lime-400">{(row.tier1 / 1000).toFixed(1)}K $</td>
                            <td className="p-2 text-right text-lime-400 font-bold">{(row.tier2 / 1000).toFixed(1)}K $</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* EcoLunch Connect future */}
                <div className="mt-4 p-3 bg-black/30 rounded-lg border border-purple-500/30">
                  <p className="text-xs text-purple-300">
                    <span className="font-semibold uppercase tracking-wider">EcoLunch Connect — </span>
                    {language === 'fr'
                      ? 'Couche de revenus additionnelle (1,0% à 2,5%) non incluse dans les prévisions principales. Sera activée lorsque le portail école et le système EcoLunch Connect seront en place.'
                      : 'Additional revenue layer (1.0% to 2.5%) not included in main forecasts. Will be activated when the school portal and EcoLunch Connect system are in place.'}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-lime-400 mb-4">
                  {language === 'fr' ? 'Formules Clés' : 'Key Formulas'}
                </h3>
                <div className="space-y-3">
                  {formulas.map((item, idx) => {
                    const colors = ['lime', 'cyan', 'orange', 'lime'];
                    return (
                      <div key={idx} className={`p-4 md:p-5 bg-black/40 rounded-lg border border-${colors[idx]}-500/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 relative group`}>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-sm text-gray-300">{item.formula}</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className={`h-4 w-4 text-${colors[idx]}-400/60 hover:text-${colors[idx]}-400 cursor-help transition-colors`} />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs bg-gray-900 border border-gray-700 text-gray-200">
                              <p className="text-sm">{item.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <code className={`text-xs sm:text-sm font-mono text-${colors[idx]}-400 bg-black/60 px-3 py-1.5 rounded break-all`}>{item.calc}</code>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-cyan-400 mb-4">
                  {language === 'fr' ? 'Prix par Repas (évolution)' : 'Meal Price (evolution)'}
                </h3>
                <div className="flex flex-wrap items-center gap-3">
                  {Object.entries(tauxParAnnee).map(([year, t], idx) => {
                    const colors = ['lime', 'cyan', 'orange', 'purple', 'lime'];
                    return (
                      <React.Fragment key={year}>
                        {idx > 0 && <span className="text-xl text-gray-600 hidden sm:block">→</span>}
                        <div className={`flex-1 min-w-[100px] p-3 bg-gradient-to-r from-${colors[idx]}-500/20 to-${colors[idx]}-500/5 rounded-lg border border-${colors[idx]}-500/30`}>
                          <p className="text-xs text-gray-400 mb-1">{year}</p>
                          <p className={`text-2xl font-bold text-${colors[idx]}-400`}>{t.prix}$</p>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              <div className="p-5 sm:p-6 bg-gradient-to-br from-lime-500/10 to-transparent rounded-xl border border-lime-500/30">
                <h3 className="text-xl font-bold text-lime-400 mb-4">
                  {language === 'fr' ? 'Structure Revenus' : 'Revenue Structure'}
                </h3>
                <div className="space-y-3">
                  <p className="text-sm sm:text-base text-gray-300 flex items-center gap-2 leading-relaxed">
                    <Percent className="h-4 w-4 text-lime-500 flex-shrink-0" /> 
                    <span>{language === 'fr' ? 'FinTech' : 'FinTech'}: {ftMin}% → {ftMax}% GMV {language === 'fr' ? 'régulier' : 'regular'}</span>
                  </p>
                  <p className="text-sm sm:text-base text-gray-300 flex items-center gap-2 leading-relaxed">
                    <Percent className="h-4 w-4 text-cyan-500 flex-shrink-0" /> 
                    <span>Sezzle: {(SEZZLE_NET * 100).toFixed(1)}% GMV Sezzle ({language === 'fr' ? 'uplift' : 'uplift'} {sezzMin}% → {sezzMax}%)</span>
                  </p>
                  <p className="text-sm sm:text-base text-gray-300 flex items-center gap-2 leading-relaxed">
                    <DollarSign className="h-4 w-4 text-orange-500 flex-shrink-0" /> 
                    <span>{tauxParAnnee.Y1.ftFee}$ → {tauxParAnnee.Y5.ftFee}$ / transaction</span>
                  </p>
                  <p className="text-sm sm:text-base text-gray-300 flex items-center gap-2 leading-relaxed">
                    <DollarSign className="h-4 w-4 text-lime-500 flex-shrink-0" /> 
                    <span>App: {APP_PRIX}$/mois x {appMin}% → {appMax}% {language === 'fr' ? 'des parents' : 'of parents'}</span>
                  </p>
                  <p className="text-sm sm:text-base text-gray-300 flex items-center gap-2 leading-relaxed">
                    <TrendingUp className="h-4 w-4 text-cyan-500 flex-shrink-0" /> 
                    <span>SaaS: {SAAS_PRIX}$ / {language === 'fr' ? 'traiteur' : 'caterer'} / {language === 'fr' ? 'mois' : 'month'} (M4+)</span>
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-orange-400 mb-4">{language === 'fr' ? 'Structure de coûts pré-seed' : 'Pre-seed cost structure'}</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { label: language === 'fr' ? 'GTM/mois (M1-M6)' : 'GTM/month (M1-M6)', value: 58333, color: 'lime' },
                    { label: language === 'fr' ? 'Total GTM 6 mois' : 'Total GTM 6 months', value: 350000, color: 'lime' },
                    { label: language === 'fr' ? 'Dépenses/mois M1-M6' : 'Expenses/month M1-M6', value: 83300, color: 'cyan' },
                    { label: language === 'fr' ? 'Dépenses/mois M7-M12' : 'Expenses/month M7-M12', value: 66300, color: 'orange' },
                    { label: language === 'fr' ? 'Total pré-seed' : 'Total pre-seed', value: 500000, color: 'purple' },
                    { label: language === 'fr' ? 'Réserve' : 'Reserve', value: 67000, color: 'lime' },
                  ].map((item, idx) => (
                    <Card key={idx} className={`bg-gradient-to-br from-${item.color}-500/10 to-transparent border-${item.color}-500/30`}>
                      <CardContent className="p-4">
                        <p className="text-sm text-gray-400 mb-1">{item.label}</p>
                        <p className={`text-xl font-bold text-${item.color}-400`}>{fmt(item.value)}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-gradient-to-br from-cyan-500/10 to-transparent rounded-lg border border-cyan-500/30 text-center">
                <p className="text-lg font-bold text-cyan-400">
                  {language === 'fr' ? 'BAIIA = Revenus - OPEX' : 'EBITDA = Revenue - OPEX'}
                </p>
                <p className="text-sm text-gray-400 mt-2">
                  {language === 'fr' ? 'Positif dès M6-M7 (Y1)' : 'Positive from M6-M7 (Y1)'}
                </p>
              </div>

              <NavigationControls />
            </CardContent>
          </Card>
        </div>
      </div>
    </TooltipProvider>
  );
}
